import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const Navbar = () => {
  const [menuState, setMenuState] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, isAuthenticated, signOut } = useAuth();
  const [credits, setCredits] = useState<number | null>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch user credits
  useEffect(() => {
    const fetchCredits = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('credits')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching credits:', error);
        return;
      }
      
      setCredits(data?.credits ?? 0);
    };

    fetchCredits();
  }, [user]);

  const navLinks = [
    { name: "Dashboard", href: "/dashboard" },
    { name: "Generate Images", href: "/image-generation" },
    { name: "Pricing", href: "/pricing" },
  ];

  const publicLinks = [
    { name: "Features", href: "/#features" },
    { name: "Pricing", href: "/pricing" },
    { name: "About", href: "/#about" },
  ];

  const handleSignOut = async () => {
    await signOut();
  };

  const CreditsDisplay = () => (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
      <Coins className="w-4 h-4 text-primary" />
      <span className="text-sm font-medium">{credits ?? '...'} credits</span>
    </div>
  );

  return (
    <nav className="fixed z-50 w-full">
      <div className={cn(
        'mx-auto mt-2 px-4 transition-all duration-300',
        isScrolled ? 'max-w-4xl bg-background/50 rounded-2xl border backdrop-blur-lg' : 'max-w-6xl'
      )}>
        <div className="relative flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-lg">
                DD
              </div>
              <span className="font-bold text-xl">Developers Digest</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:gap-8">
            {(isAuthenticated ? navLinks : publicLinks).map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-black dark:text-white hover:text-accent-foreground transition-colors duration-200">
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop Right Section */}
          <div className="hidden lg:flex lg:items-center lg:gap-4">
            {isAuthenticated ? (
              <>
                <CreditsDisplay />
                <Button variant="outline" size="sm" onClick={handleSignOut}>
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/signup">Sign Up</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden">
            <button
              onClick={() => setMenuState(!menuState)}
              className="inline-flex items-center justify-center p-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground">
              <span className="sr-only">Open main menu</span>
              {menuState ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={cn(
          'lg:hidden',
          menuState ? 'block' : 'hidden'
        )}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-background/80 backdrop-blur-lg rounded-lg mt-2 border">
            {(isAuthenticated ? navLinks : publicLinks).map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block px-3 py-2 rounded-md text-base font-medium text-black dark:text-white hover:text-accent-foreground hover:bg-accent/10"
                onClick={() => setMenuState(false)}>
                {link.name}
              </Link>
            ))}
            <div className="pt-4 pb-3 border-t border-border">
              {isAuthenticated ? (
                <div className="space-y-3 px-3">
                  <CreditsDisplay />
                  <Button variant="outline" size="sm" onClick={handleSignOut} className="w-full">
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 px-3">
                  <Button asChild variant="ghost" size="sm" className="w-full">
                    <Link to="/login">Login</Link>
                  </Button>
                  <Button asChild size="sm" className="w-full">
                    <Link to="/signup">Sign Up</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
